/**
 * Firebase auth loader — dynamically imports Firebase SDK from CDN.
 * Self-contained: no AppLib/AppServices dependencies (must work on login.html).
 */

const CDN = 'https://www.gstatic.com/firebasejs/10.14.1';

export interface FirebaseAuthInstance {
  signInWithGoogle(): Promise<string>; // Returns Firebase ID token
  signInWithApple(): Promise<string>;     // Returns Firebase ID token
  signInWithMicrosoft(): Promise<string>; // Returns Firebase ID token
}

async function fetchFirebaseConfig(): Promise<Record<string, unknown> | null> {
  try {
    const resp = await fetch('/api/auth/firebase-config');
    if (!resp.ok) return null;
    const data = await resp.json();
    return data.enabled ? data.config : null;
  } catch {
    return null;
  }
}

export async function loadFirebase(): Promise<FirebaseAuthInstance | null> {
  try {
    const config = await fetchFirebaseConfig();
    if (!config) return null;

    // Dynamic CDN imports — @vite-ignore prevents Vite from trying to bundle them
    /* eslint-disable @typescript-eslint/no-explicit-any */
    const { initializeApp } = (await import(/* @vite-ignore */ `${CDN}/firebase-app.js`)) as any;
    const { getAuth, signInWithPopup, GoogleAuthProvider, OAuthProvider } = (await import(
      /* @vite-ignore */ `${CDN}/firebase-auth.js`
    )) as any;
    /* eslint-enable @typescript-eslint/no-explicit-any */

    const app = initializeApp(config);
    const auth = getAuth(app);

    return {
      async signInWithGoogle(): Promise<string> {
        const result = await signInWithPopup(auth, new GoogleAuthProvider());
        return result.user.getIdToken() as Promise<string>;
      },
      async signInWithApple(): Promise<string> {
        const result = await signInWithPopup(auth, new OAuthProvider('apple.com'));
        return result.user.getIdToken() as Promise<string>;
      },
      async signInWithMicrosoft(): Promise<string> {
        const result = await signInWithPopup(auth, new OAuthProvider('microsoft.com'));
        return result.user.getIdToken() as Promise<string>;
      },
    };
  } catch {
    // Firebase not configured or CDN unavailable — degrade silently
    return null;
  }
}

export async function exchangeFirebaseToken(
  firebaseToken: string,
  mode?: string,
): Promise<{ requires_tenant_selection: boolean }> {
  const resp = await fetch('/api/auth/firebase-login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ firebase_token: firebaseToken, ...(mode ? { mode } : {}) }),
  });

  if (!resp.ok) {
    const err = await resp.json().catch(() => ({}));
    throw new Error((err as { detail?: string }).detail || 'Authentication failed. Please try again.');
  }

  return resp.json() as Promise<{ requires_tenant_selection: boolean }>;
}
